import apiService from "../Services/api.service";

const inr = (n) =>
  n === null || n === undefined || n === ""
    ? "—"
    : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const orderLine = (item) => item.clinicName || "";

const opts = (...labels) =>
  labels.map((l) =>
    typeof l === "string" ? { value: l, label: l } : l,
  );


const today = () => new Date().toISOString().split("T")[0];

const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
};

// Expense claims store amounts as integer paise — this file's own inr()
// takes rupees, so claims need their own rupee-from-paise formatter.
const rupeeFromPaise = (paise) =>
  paise === null || paise === undefined
    ? "—"
    : `₹${(Number(paise) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;


export const ACTION_SCREENS = {


  /* SL3. The gate every order passes through. */
  "approve-agreement": (item) => ({
    title: "Approve purchase agreement",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "w",
        h: "This gate holds the order. Every order passes through it and no response SLA is set.",
      },
      {
        t: "two",
        a: { t: "ro", k: "Signatory", v: item.context?.signatoryName },
        b: { t: "ro", k: "Signatory email", v: item.context?.signatoryEmail },
      },
      {
        t: "two",
        a: { t: "ro", k: "Place of supply", v: item.context?.placeOfSupply },
        b: { t: "ro", k: "Order total", v: inr(item.context?.orderTotal) },
      },
      { t: "ro", k: "Payment terms", v: item.context?.paymentTerm },
      { t: "head", h: "Generated document" },
      {
        t: "link",
        k: "Purchase agreement",
        s: item.context?.documentFileName || "Open to review before approving",
        href: item.context?.documentUrl,
        l: "Open PDF",
      },
      {
        t: "chips",
        name: "decision",
        l: "Decision",
        opts: opts(
          { value: "approve", label: "Approve" },
          { value: "request_changes", label: "Request changes" },
        ),
      },
      {
        t: "cbx",
        name: "checked",
        l: "Payment terms, signatory and place of supply match the quotation",
      },
      {
        t: "ta",
        name: "changeRequest",
        l: "Change request, required if not approving",
      },
      {
        t: "open",
        h: "No response SLA is set on this gate, so an order can sit here indefinitely.",
      },
    ],
    validate: (v) => {
      if (v.decision === "request_changes")
        return String(v.changeRequest || "").trim()
          ? null
          : "Describe the changes needed";
      return v.checked
        ? null
        : "Confirm the agreement matches the quotation before approving";
    },
    primary: "Save decision",
    secondary: "Cancel",
    success: "Decision recorded",
    submit: (item, v, token) =>
      apiService.approveAgreement(item.orderId, v, token),
  }),

  /* ============================================================
     SECTION 5, onboarding and mockup
     ============================================================ */

  /* EO1 */
  "offer-slots": (item) => ({
    title: "Offer onboarding call slots",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "g",
        h: "Agreement signed. Engage credentials have gone to the customer.",
      },
      {
        t: "chips",
        name: "callLengthMins",
        l: "Call length",
        on: 1,
        opts: opts(
          { value: 30, label: "30 min" },
          { value: 45, label: "45 min" },
          { value: 60, label: "60 min" },
        ),
      },
      {
        t: "two",
        a: { t: "date", name: "from", l: "Offer slots from", v: today() },
        b: { t: "date", name: "to", l: "Offer slots to", v: plusDays(4) },
      },
      {
        t: "sel",
        name: "slotStrategy",
        l: "Slots to offer",
        opts: opts(
          { value: "next_6", label: "Next 6 available in my calendar" },
          { value: "morning", label: "Morning slots only" },
          { value: "afternoon", label: "Afternoon slots only" },
          { value: "manual", label: "Pick manually" },
        ),
      },
      { t: "ta", name: "note", l: "Note to the customer" },
      { t: "open", h: "Chase cadence for an unselected slot is undefined." },
    ],
    validate: (v) =>
      !v.from || !v.to
        ? "Pick the date range to offer slots from"
        : v.to < v.from
          ? "The end date cannot be before the start date"
          : null,
    primary: "Offer slots",
    secondary: "Cancel",
    success: "Slots offered to the customer",
    submit: (item, v, token) =>
      apiService.offerOnboardingSlots(item.orderId, v, token),
  }),

  /* EO2 */
  "confirm-attendance": (item) => ({
    title: "Customer selected a slot, confirm attendance",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "i",
        h: `Customer picked ${item.context?.slotPicked || "a slot"}.`,
      },
      {
        t: "two",
        a: { t: "ro", k: "Slot", v: item.context?.slotPicked },
        b: { t: "ro", k: "Call length", v: item.context?.callLengthMins },
      },
      {
        t: "cbx",
        name: "confirmed",
        l: "I will attend this call",
        s: "The customer receives a calendar invite.",
      },
      { t: "ta", name: "note", l: "Note" },
    ],
    validate: (v) => (v.confirmed ? null : "Confirm attendance before saving"),
    primary: "Confirm call",
    secondary: "Cancel",
    success: "Call confirmed",
    submit: (item, v, token) =>
      apiService.confirmCallAttendance(item.orderId, v, token),
  }),

  /* EO3 */
  "chase-slot": (item) => ({
    title: "Customer has not picked a slot",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "w",
        h:
          item.context?.slotsOfferedDaysAgo !== undefined &&
          item.context?.slotsOfferedDaysAgo !== null
            ? `Slots offered ${item.context.slotsOfferedDaysAgo} days ago. None selected.`
            : "Slots offered. None selected.",
      },
      {
        t: "sel",
        name: "action",
        l: "Action",
        opts: opts(
          { value: "resend", label: "Resend slot options" },
          { value: "new_dates", label: "Offer new dates" },
          { value: "logged_call", label: "Log a phone call" },
          { value: "assign_directly", label: "Assign a slot directly" },
        ),
      },
      { t: "ta", name: "note", l: "Note" },
    ],
    primary: "Log follow-up",
    secondary: "Close",
    success: "Follow-up logged",
    submit: (item, v, token) =>
      apiService.logOnboardingFollowUp(item.orderId, v, token),
  }),

  /* Ops Associate fallback — customer sent the logo some other way (email,
     WhatsApp) instead of uploading it themselves through Engage. */
  "upload-logo": (item) => ({
    title: "Upload clinic logo",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "i",
        h: "The customer hasn't uploaded a logo yet. Upload it on their behalf.",
      },
      {
        t: "up",
        name: "logo",
        l: "Clinic logo",
        s: "PNG or JPG, ideally on a transparent or white background.",
        accept: "image/*",
      },
    ],
    validate: (v) => (!v.logo || !v.logo.length ? "Choose a logo file" : null),
    primary: "Upload logo",
    secondary: "Cancel",
    success: "Logo uploaded",
    submit: (item, v, token) => apiService.opsUploadClinicLogo(item.orderId, v.logo[0], token),
  }),

  /* ============================================================
     SECTION 6, dispatch and balance payment
     ============================================================ */

  /* OA6 */
  "set-dates": (item) => {
    const isEmi = item.context?.paymentType === "emi";

    return {
      title: "Set shipping and balance due dates",
      sub: orderLine(item),
      body: [
        { t: "alert", k: "g", h: "Mockup approved by the customer." },
        isEmi
          ? {
              t: "date",
              name: "shippingDate",
              l: "Planned shipping date",
              req: 1,
            }
          : {
              t: "two",
              a: {
                t: "date",
                name: "shippingDate",
                l: "Planned shipping date",
                req: 1,
              },
              b: {
                t: "date",
                name: "balanceDueDate",
                l: "Balance payment due",
                req: 1,
              },
            },
        {
          t: "sel",
          name: "dispatchOrigin",
          l: "Dispatch origin",
          opts: opts("Pune warehouse", "Contract manufacturer, direct"),
        },
        {
          t: "txt",
          name: "serial",
          l: "Unit serial number",
          hint: "Leave blank if not yet allocated.",
        },
        {
          t: "cbx",
          name: "publishToCustomer",
          l: "Publish both dates to the customer's order tracking tab",
          on: true,
        },
      ],
      validate: (v) =>
        !v.shippingDate || (!isEmi && !v.balanceDueDate)
          ? isEmi
            ? "Shipping date is required"
            : "Both dates are required"
          : null,
      primary: "Save dates",
      secondary: "Cancel",
      success: "Dates saved",
      submit: (item, v, token) =>
        apiService.setShippingDates(item.orderId, v, token),
    };
  },

  /* OA7 */
  "ready-to-ship": (item) => {
    const isEmi = item.context?.paymentType === "emi";
    const emiApproved = item.context?.emiStatus === "approved";

    // Defaults to the balance due date Ops Associate already set at OA6
    // (set-dates) — falls back to +14 days only if that was somehow never set.
    const defaultExpiry = item.context?.balanceDueDate
      ? new Date(item.context.balanceDueDate)
      : (() => {
          const d = new Date();
          d.setDate(d.getDate() + 14);
          return d;
        })();

    return {
      title: "Mark ready to ship",
      sub: orderLine(item),
      body: [
        isEmi
          ? {
              t: "alert",
              k: emiApproved ? "w" : "d",
              h: emiApproved
                ? "This order is financed via Massfintech — no balance link is sent to the doctor. Confirm the disbursement has been received below."
                : "EMI is not yet approved by Massfintech. Get it approved before marking this order ready to ship.",
            }
          : {
              t: "alert",
              k: "w",
              h: "This triggers the balance payment request, reminded twice daily until paid.",
            },
        isEmi
          ? { t: "ro", k: "Unit serial", v: item.context?.serial }
          : {
              t: "two",
              a: { t: "ro", k: "Unit serial", v: item.context?.serial },
              b: { t: "ro", k: "Balance due", v: inr(item.context?.balanceDue) },
            },
        { t: "txt", name: "dwName", l: "DW name", req: 1 },
        {
          t: "cbx",
          name: "qcPassed",
          l: "Unit built, QC passed and branding applied",
        },
        {
          t: "cbx",
          name: "bagIncluded",
          l: "Carry bag included as per the quotation",
          s: item.context?.bagRequired
            ? "The quotation includes a carry bag."
            : "The quotation does not include a carry bag on this order.",
        },
        {
          t: "cbx",
          name: "sendBalanceLink",
          l: "Send the balance payment link now",
          on: !isEmi,
          dis: isEmi,
          s: isEmi
            ? "Not applicable — this order is financed via EMI, not paid directly by the doctor."
            : "Optional — leave unchecked to mark ready to ship without sending a link yet; one can still be shared later.",
        },
        ...(isEmi
          ? [
              {
                t: "cbx",
                name: "emiPaymentConfirmed",
                l: "EMI payment completed through Massfintech",
                s: "Confirms the financed balance has actually been disbursed to scanO before this order ships.",
              },
            ]
          : [
              {
                t: "number",
                name: "balanceAmount",
                l: "Balance payment amount",
                v: item.context?.balanceDue,
                hint: "Defaults to the full balance due — lower it to request a partial payment instead.",
              },
              {
                t: "date",
                name: "balanceDueDate",
                l: "Link expiry date",
                v: defaultExpiry.toISOString().split("T")[0],
                hint: "Defaults to the balance due date set earlier — change it if you want a different expiry.",
              },
            ]),
      ],
      validate: (v) =>
        !v.qcPassed
          ? "Confirm the unit has passed QC"
          : !String(v.dwName || "").trim()
            ? "DW name is required"
            : isEmi
              ? !emiApproved
                ? "EMI must be approved by Massfintech first"
                : !v.emiPaymentConfirmed
                  ? "Confirm the EMI payment has been received from Massfintech"
                  : null
              : !v.sendBalanceLink
                ? null
                : !v.balanceAmount || Number(v.balanceAmount) <= 0
                  ? "Enter a valid balance amount"
                  : Number(v.balanceAmount) > Number(item.context?.balanceDue || 0)
                    ? "Amount cannot exceed the outstanding balance"
                    : !v.balanceDueDate
                      ? "Link expiry date is required"
                      : null,
      primary: "Mark ready to ship",
      secondary: "Cancel",
      success: "Marked ready to ship",
      submit: (item, v, token) =>
        apiService.markReadyToShip(item.orderId, v, token),
    };
  },

  /* Ops Associate — explicitly (re)issue the balance link with a chosen
     expiry, distinct from markReadyToShip's quick auto-send. */
  "share-balance-link": (item) => {
    const defaultExpiry = item.context?.balanceDueDate
      ? new Date(item.context.balanceDueDate)
      : (() => {
          const d = new Date();
          d.setDate(d.getDate() + 14);
          return d;
        })();

    return {
      title: "Share balance payment link",
      sub: orderLine(item),
      body: [
        {
          t: "ro",
          k: "Unit serial",
          v: item.context?.serial,
        },
        {
          t: "number",
          name: "amount",
          l: "Balance payment amount",
          v: item.context?.balanceDue,
          hint: "Defaults to the full balance due — lower it to request a partial payment instead.",
        },
        {
          t: "date",
          name: "dueDate",
          l: "Link expiry date",
          v: defaultExpiry.toISOString().split("T")[0],
          hint: "Defaults to the balance due date set earlier — change it if you want a different expiry.",
        },
        {
          t: "open",
          h: "If a link is already open and unpaid, this resends its notification rather than issuing a new one with this amount/expiry.",
        },
      ],
      validate: (v) =>
        !v.amount || Number(v.amount) <= 0
          ? "Enter a valid balance amount"
          : Number(v.amount) > Number(item.context?.balanceDue || 0)
            ? "Amount cannot exceed the outstanding balance"
            : !v.dueDate
              ? "Link expiry date is required"
              : null,
      primary: "Share link",
      secondary: "Cancel",
      success: "Balance payment link shared",
      submit: (item, v, token) =>
        apiService.shareBalancePaymentLink(item.orderId, v.amount, v.dueDate, token),
    };
  },

  /* Ops Associate raises the invoice any time after ready-to-ship, even with
     the balance still outstanding. GSTIN is optional — most clinics aren't
     GST-registered, so blank just means unregistered, not incomplete. */
  "generate-invoice": (item) => ({
    title: "Generate invoice",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "w",
        h: "Raises the tax invoice now — the balance doesn't need to be cleared first.",
      },
      {
        t: "txt",
        name: "gstin",
        l: "Customer GSTIN",
        v: item.context?.gstin || "",
        hint: "Ask the customer — leave blank if they aren't GST-registered.",
      },
    ],
    primary: "Generate invoice",
    secondary: "Cancel",
    success: "Invoice generated",
    submit: (item, v, token) =>
      apiService.generateInvoiceEarly(item.orderId, v.gstin, token),
  }),

  /* OA8 */
  "order-hold": (item) => ({
    title: "Order held, balance outstanding",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "d",
        h: `Marked ready to ship${
          item.context?.readyDaysAgo != null
            ? ` ${item.context.readyDaysAgo} days ago`
            : ""
        }. Balance outstanding. Dispatch is blocked.`,
      },
      {
        t: "sum",
        rows: [
          ["Order total", inr(item.context?.orderTotal)],
          ["Advance received", inr(item.context?.advanceReceived)],
          ["Balance outstanding", inr(item.context?.balanceDue), "tot"],
        ],
      },
      { t: "ro", k: "Reminder status", v: item.context?.reminderStatus },
      {
        t: "chips",
        name: "decision",
        l: "Action",
        opts: opts(
          { value: "hold", label: "Keep holding" },
          { value: "release", label: "Release on approval" },
          { value: "cancel", label: "Cancel the order" },
        ),
      },
      {
        t: "ta",
        name: "reason",
        l: "Reason, required if releasing or cancelling",
      },
    ],
    validate: (v) =>
      v.decision !== "hold" && !String(v.reason || "").trim()
        ? "A reason is required to release or cancel"
        : null,
    primary: "Save decision",
    secondary: "Close",
    success: "Decision saved",
    submit: (item, v, token) =>
      apiService.resolveOrderHold(item.orderId, v, token),
  }),

  /* OA9 */
  dispatch: (item) => ({
    title: "Confirm shipment",
    sub: orderLine(item),
    body: [
      { t: "alert", k: "g", h: "Balance cleared and reconciled by Accounts." },
      {
        t: "two",
        a: {
          t: "sel",
          name: "courier",
          l: "Courier",
          opts: opts("Blue Dart", "Delhivery", "Gati express- (all cargo logistics)", "Own vehicle"),
        },
        b: { t: "txt", name: "trackingId", l: "AWB Number", req: 1 },
      },
      {
        t: "two",
        a: {
          t: "date",
          name: "dispatchDate",
          l: "Dispatch date",
          req: 1,
          v: today(),
        },
        b: { t: "date", name: "expectedDelivery", l: "Expected delivery" },
      },
      {
        t: "cbx",
        name: "publishTracking",
        l: "Publish a clickable AWB number to the customer's order tab",
        on: true,
      },
      {
        t: "cbx",
        name: "notifyOpsLead",
        l: "Notify Ops Lead to assign a technician",
        on: true,
      },
    ],
    validate: (v) =>
      !String(v.trackingId || "").trim()
        ? "An AWB number is required"
        : !v.dispatchDate
          ? "A dispatch date is required"
          : !v.publishTracking
            ? "Publishing the AWB number to the customer's order tab is required"
            : !v.notifyOpsLead
              ? "Notifying Ops Lead to assign a technician is required"
              : null,
    primary: "Confirm shipped",
    secondary: "Cancel",
    success: "Order marked shipped",
    submit: (item, v, token) =>
      apiService.confirmShipped(item.orderId, v, token),
  }),

  "edit-fulfilment": (item) => ({
    title: "Edit fulfilment details",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "i",
        h: "Admin override — corrects details already recorded, doesn't re-trigger dispatch notifications.",
      },
      {
        t: "two",
        a: { t: "date", name: "shippingDate", l: "Shipping date", v: item.context?.shippingDate },
        b: {
          t: "sel",
          name: "dispatchOrigin",
          l: "Dispatch origin",
          opts: opts("Pune warehouse", "Contract manufacturer, direct"),
          v: item.context?.dispatchOrigin,
        },
      },
      { t: "txt", name: "serial", l: "Unit serial number", v: item.context?.serial },
      {
        t: "two",
        a: {
          t: "sel",
          name: "courier",
          l: "Courier",
          opts: opts("Blue Dart", "Delhivery", "Gati express- (all cargo logistics)", "Own vehicle"),
          v: item.context?.courier,
        },
        b: { t: "txt", name: "trackingId", l: "AWB Number", v: item.context?.trackingId },
      },
      {
        t: "two",
        a: { t: "date", name: "dispatchDate", l: "Dispatch date", v: item.context?.dispatchDate },
        b: { t: "date", name: "expectedDelivery", l: "Expected delivery", v: item.context?.expectedDelivery },
      },
    ],
    primary: "Save changes",
    secondary: "Cancel",
    success: "Fulfilment details updated",
    submit: (item, v, token) => apiService.editDispatchDetails(item.orderId, v, token),
  }),

  /* AC3 */
  reconcile: (item) => ({
    title: "Reconcile receipt",
    sub: orderLine(item),
    body: [
      {
        t: "two",
        a: { t: "ro", k: "Amount received", v: inr(item.context?.amount) },
        b: { t: "ro", k: "Received on", v: item.context?.receivedOn },
      },
      {
        t: "two",
        a: { t: "ro", k: "Payment reference", v: item.context?.reference },
        b: { t: "ro", k: "Mode", v: item.context?.mode },
      },
      { t: "txt", name: "booksReceiptNumber", l: "Zoho Books receipt number" },
      {
        t: "cbx",
        name: "matched",
        l: "Matched to the order and posted in Books",
      },
    ],
    validate: (v) =>
      v.matched ? null : "Confirm the receipt is posted in Books",
    primary: "Mark reconciled",
    secondary: "Raise a query",
    success: "Receipt reconciled",
    submit: (item, v, token) =>
      apiService.reconcileReceipt(item.orderId, v, token),
  }),

  /* AC4 */
  "clear-balance": (item) => ({
    title: "Confirm balance cleared for dispatch",
    sub: orderLine(item),
    body: [
      { t: "alert", k: "i", h: "Dispatch is blocked until you record clearance." },
      {
        t: "sum",
        rows: [
          ["Order total", inr(item.context?.orderTotal)],
          ["Advance received", inr(item.context?.advanceReceived)],
          ["Balance received", inr(item.context?.balanceReceived)],
          ["Outstanding", inr(item.context?.outstanding), "tot"],
        ],
      },
      {
        t: "two",
        a: { t: "ro", k: "Payment reference", v: item.context?.reference },
        b: { t: "ro", k: "Mode", v: item.context?.mode },
      },
      {
        t: "cbx",
        name: "reconciledInBooks",
        l: "Both receipts reconciled in Zoho Books",
      },
      {
        t: "cbx",
        name: "matchesTemplate",
        l: "Amounts match the agreed payment template",
      },
    ],
    validate: (v) =>
      v.reconciledInBooks && v.matchesTemplate
        ? null
        : "Both checks are required before recording clearance",
    primary: "Record clearance",
    secondary: "Raise a query",
    success: "Balance clearance recorded",
    submit: (item, v, token) => apiService.clearBalance(item.orderId, v, token),
  }),

  /* AC5 */
  "raise-invoice": (item) => ({
    title: "Raise GST tax invoice",
    sub: orderLine(item),
    body: [
      {
        t: "open",
        h: "The tax invoice is in the prototype's task engine but absent from the flow. Confirm where it sits.",
      },
      {
        t: "two",
        a: { t: "ro", k: "Place of supply", v: item.context?.placeOfSupply },
        b: { t: "ro", k: "Supply type", v: item.context?.supplyType },
      },
      {
        t: "ro",
        k: "Invoice number",
        v: "Assigned by Zoho Books on creation",
      },
      {
        t: "date",
        name: "invoiceDate",
        l: "Invoice date",
        req: 1,
        v: item.context?.dispatchDate || today(),
        hint: "Must be the dispatch date. GST requires issue at or before removal of goods.",
      },
      {
        t: "sum",
        rows: [
          ["Taxable value", inr(item.context?.taxableValue)],
          [item.context?.taxLabel || "Tax", inr(item.context?.taxAmount)],
          ["Invoice total", inr(item.context?.invoiceTotal), "tot"],
        ],
      },
      { t: "cbx", name: "gstinVerified", l: "Customer GSTIN verified" },
    ],
    validate: (v) =>
      !v.invoiceDate
        ? "An invoice date is required"
        : !v.gstinVerified
          ? "Verify the customer GSTIN first"
          : null,
    primary: "Issue invoice",
    secondary: "Cancel",
    success: "Tax invoice issued",
    submit: (item, v, token) => apiService.raiseInvoice(item.orderId, v, token),
  }),

  /* ============================================================
     SECTION 7, installation and handover
     ============================================================ */

  /* OL3 and OL4 */
  "assign-technician": (item) => ({
    title:
      item.id === "OL4" ? "Technician declined, reassign" : "Assign technician",
    sub: [item.clinicName, item.context?.city].filter(Boolean).join(" · "),
    body: [
      {
        t: "alert",
        k: item.id === "OL4" ? "d" : "i",
        h:
          item.id === "OL4"
            ? `${item.context?.declinedBy || "The technician"} declined this job. No auto-reassignment exists.`
            : "Installation cannot be scheduled until a technician accepts.",
      },
      // --- Fetched / already-known info (read-only) ---
      {
        t: "two",
        a: { t: "ro", k: "AWB Number", v: item.context?.trackingId, href: item.context?.trackingUrl },
        b: { t: "ro", k: "Expected delivery", v: item.context?.expectedDelivery },
      },
      {
        t: "two",
        a: { t: "ro", k: "Clinic", v: item.clinicName },
        b: { t: "ro", k: "Clinic address", v: item.context?.address },
      },
      {
        t: "two",
        a: { t: "ro", k: "Clinic contact", v: item.context?.contact },
        b: { t: "ro", k: "Doctor", v: item.context?.doctorName },
      },
      {
        t: "two",
        a: { t: "ro", k: "Doctor contact", v: item.context?.doctorContact },
        b: { t: "ro", k: "Activation code", v: item.context?.activationCode },
      },

      // --- Ops input ---
      {
        t: "sel",
        name: "technicianId",
        l: "Technician",
        opts: (item.context?.technicians || []).map((t) => ({
          value: t.id,
          label: `${t.name}, ${t.base}, ${t.openJobs} open jobs`,
        })),
        hint: "Base city and current load are shown for each.",
      },
      {
        t: "txt",
        name: "installationWindow",
        l: "Installation window",
        hint: 'Free text, e.g. "14 to 16 Aug" — shown to the technician as-is.',
      },
      {
        t: "txt",
        name: "acceptByHours",
        l: "Accept-by deadline, hours",
        hint: "After this the job is offered to the next technician.",
      },
      {
        t: "txt",
        name: "mapsLink",
        l: "Google Maps location",
        v: item.context?.mapsLink,
        hint: "Paste the clinic's Google Maps link — shown to the technician.",
      },
      { t: "ta", name: "siteNotes", l: "Site notes", v: item.context?.siteNotes },
      {
        t: "alert",
        k: "i",
        h: (
          <>
            <div>
              <strong>Cheque</strong> — of same date, favoring "Trismus Healthcare Technologies Pvt Ltd".
            </div>
            <div style={{ marginTop: 6 }}>
              <strong>Via NEFT</strong> — on the following bank account:
            </div>
            <div style={{ marginTop: 4, paddingLeft: 12 }}>
              Account Name: Trismus Healthcare Technologies Pvt. Ltd.
              <br />
              Account Number: 3945264919
              <br />
              Bank Name: Kotak Mahindra Bank Ltd.
              <br />
              Branch: Baner, Pune
              <br />
              IFSC: KKBK0001767
            </div>
          </>
        ),
      },
      { t: "ta", name: "paymentDue", l: "Payment due", v: item.context?.paymentDue },
      {
        t: "open",
        h: "Response SLA and auto-reassignment are not in the flow. The order stalls silently without them.",
      },
    ],
    validate: (v) => (v.technicianId ? null : "Pick a technician"),
    primary: "Offer job",
    secondary: "Cancel",
    success: "Job offered to the technician",
    submit: (item, v, token) =>
      apiService.assignTechnician(item.orderId, v, token),
  }),

  /* OL5 */
  "plan-travel": (item) => ({
    title: "Plan travel and set arrival date",
    sub: [item.clinicName, item.context?.city].filter(Boolean).join(" · "),
    body: [
      {
        t: "alert",
        k: "g",
        h: `${item.context?.technicianName || "The technician"} accepted this job.`,
      },
      {
        t: "two",
        a: { t: "date", name: "arrivalDate", l: "Arrival date", req: 1 },
        b: {
          t: "sel",
          name: "slot",
          l: "Slot",
          opts: opts(
            "Morning, 9 to 12",
            "Afternoon, 1 to 4",
            "Evening, 4 to 7",
          ),
        },
      },
      {
        t: "sel",
        name: "travelMode",
        l: "Travel mode",
        opts: opts(
          "Train",
          "Flight",
          "Road, company vehicle",
          "Road, reimbursed",
        ),
      },
      {
        t: "cbx",
        name: "publishArrival",
        l: "Publish the arrival date to the customer's order tracking tab",
        on: true,
      },
      {
        t: "cbx",
        name: "notifyEngagementOfficer",
        l: "Notify the Engagement Officer to set the inauguration date",
        on: true,
      },
    ],
    validate: (v) =>
      !v.arrivalDate
        ? "An arrival date is required"
        : !v.publishArrival
          ? "Publishing the arrival date to the customer's order tracking tab is required"
          : !v.notifyEngagementOfficer
            ? "Notifying the Engagement Officer to set the inauguration date is required"
            : null,
    primary: "Confirm arrival date",
    secondary: "Cancel",
    success: "Arrival date confirmed",
    submit: (item, v, token) => apiService.planTravel(item.orderId, v, token),
  }),

  /* OL6 */
  "confirm-inauguration": (item) => ({
    title: "Confirm inauguration date",
    sub: orderLine(item),
    body: [
      {
        t: "two",
        a: { t: "ro", k: "Technician arrival", v: item.context?.arrival },
        b: {
          t: "ro",
          k: "Engagement Officer",
          v: item.context?.engagementOfficer,
        },
      },
      {
        t: "date",
        name: "inaugurationDate",
        l: "Inauguration date",
        req: 1,
      },
      {
        t: "sel",
        name: "slot",
        l: "Slot",
        opts: opts(
          "Same day as installation",
          "Day after installation",
          "Customer to choose",
        ),
      },
      { t: "ta", name: "note", l: "Note to Engagement Officer" },
    ],
    validate: (v) =>
      v.inaugurationDate ? null : "An inauguration date is required",
    primary: "Send to Engagement Officer",
    secondary: "Cancel",
    success: "Inauguration date sent",
    submit: (item, v, token) =>
      apiService.confirmInauguration(item.orderId, v, token),
  }),

  /* EO4 */
  "assign-influencer": (item) => ({
    title: "Assign influencer for inauguration",
    sub: [item.clinicName, item.context?.city].filter(Boolean).join(" · "),
    body: [
      {
        t: "two",
        a: {
          t: "ro",
          k: "Inauguration date",
          v: item.context?.inaugurationDate,
        },
        b: { t: "ro", k: "Technician", v: item.context?.technicianName },
      },
      {
        t: "sel",
        name: "arrangement",
        l: "Arrangement",
        opts: opts(
          { value: "named", label: "Named influencer" },
          { value: "clinic_led", label: "No influencer, clinic-led" },
          { value: "customer_arranged", label: "Customer to arrange" },
        ),
      },
      {
        t: "txt",
        name: "influencer",
        l: "Influencer name",
        hint: "Leave blank unless the arrangement is a named influencer.",
      },
      { t: "number", name: "budget", l: "Budget approved" },
      {
        t: "cbx",
        name: "notifyCustomer",
        l: "Notify the customer with the event details",
        on: true,
      },
      {
        t: "open",
        h: "The customer has no confirmation step for the inauguration date.",
      },
    ],
    validate: (v) =>
      v.arrangement === "named" && !String(v.influencer || "").trim()
        ? "Name the influencer, or change the arrangement"
        : null,
    primary: "Assign and notify",
    secondary: "Cancel",
    success: "Influencer assigned",
    submit: (item, v, token) =>
      apiService.assignInfluencer(item.orderId, v, token),
  }),

  /* EO5 */
  "accept-handover": (item) => ({
    title: "Accept case handover",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "g",
        h: "Installation complete, feedback submitted and warranty card issued.",
      },
      {
        t: "two",
        a: { t: "ro", k: "Installed on", v: item.context?.installedOn },
        b: { t: "ro", k: "Technician", v: item.context?.technicianName },
      },
      { t: "ro", k: "Feedback", v: item.context?.feedback },
      {
        t: "cbx",
        name: "accepted",
        l: "I accept ownership of this account as the named point of contact",
        s: "Your details appear in the customer's scanO app.",
      },
      { t: "ta", name: "followUpPlan", l: "First follow-up plan" },
    ],
    validate: (v) =>
      v.accepted ? null : "Accept ownership before saving, or reassign",
    primary: "Accept handover",
    secondary: "Reassign",
    success: "Handover accepted",
    submit: (item, v, token) =>
      apiService.acceptHandover(item.orderId, v, token),
  }),

  /* EO6 */
  "introduce-customer": (item) => ({
    title: "Introduce yourself to the customer",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "i",
        h: "You are now the named point of contact on this account.",
      },
      {
        t: "sel",
        name: "channel",
        l: "Channel",
        opts: opts("Email", "WhatsApp", "Phone call"),
      },
      { t: "ta", name: "message", l: "Introduction message" },
    ],
    validate: (v) =>
      String(v.message || "").trim() ? null : "Write an introduction first",
    primary: "Send introduction",
    secondary: "Cancel",
    success: "Introduction sent",
    submit: (item, v, token) =>
      apiService.introduceToCustomer(item.orderId, v, token),
  }),

  /* TC1 */
  "job-offer": (item) => ({
    title: "Installation job offered",
    sub: [item.clinicName, item.context?.city].filter(Boolean).join(" · "),
    body: [
      {
        t: "alert",
        k: "d",
        h: item.context?.acceptByHours
          ? `Respond within ${item.context.acceptByHours} hours. After that the job goes to another technician.`
          : "Respond promptly. The order is stalled until you answer.",
      },
      {
        t: "two",
        a: { t: "ro", k: "Window", v: item.context?.window },
        b: { t: "ro", k: "Product", v: item.context?.product },
      },
      {
        t: "two",
        a: { t: "ro", k: "Clinic", v: item.context?.clinicName },
        b: { t: "ro", k: "Address", v: item.context?.address },
      },
      {
        t: "two",
        a: { t: "ro", k: "Clinic contact", v: item.context?.contact },
        b: {
          t: "ro",
          k: "Map location",
          v: item.context?.mapsLink ? "Open" : null,
          href: item.context?.mapsLink,
        },
      },
      {
        t: "two",
        a: { t: "ro", k: "Doctor", v: item.context?.doctorName },
        b: { t: "ro", k: "Doctor number", v: item.context?.doctorContact },
      },
      {
        t: "two",
        a: { t: "ro", k: "AWB Number", v: item.context?.trackingId, href: item.context?.trackingUrl },
        b: { t: "ro", k: "Expected delivery", v: item.context?.expectedDelivery },
      },
      { t: "ro", k: "Activation code", v: item.context?.activationCode },
      { t: "ro", k: "Site notes", v: item.context?.siteNotes },
      { t: "ro", k: "Payment due", v: item.context?.paymentDue },
      {
        t: "chips",
        name: "response",
        l: "Your response",
        opts: opts(
          { value: "accept", label: "Accept" },
          { value: "conflict", label: "Flag conflict" },
        ),
      },
      { t: "ta", name: "reason", l: "Reason, required if flagging a conflict" },
    ],
    validate: (v) =>
      v.response === "conflict" && !String(v.reason || "").trim()
        ? "A reason is required to flag a conflict"
        : null,
    primary: "Submit response",
    secondary: "Cancel",
    success: "Response recorded",
    submit: (item, v, token) => apiService.respondToJob(item.orderId, v, token),
  }),

  /* TC3 — the code is already known and fixed on the clinic's kiosk record,
     so it's shown outright rather than asking the customer for it and
     typing it back in. Just enter it on the physical unit.

     The unit's own activation (temporary activated / fully activated) is set
     elsewhere, by the kiosk itself, not by this screen — Activate Air Unit
     stays disabled until that's already happened. */
  activation: (item) => {
    const isUnitActivated = ["temporary activated", "fully activated"].includes(
      item.context?.activationStatus,
    );

    return {
      title: "Enter activation code",
      sub: orderLine(item),
      body: [
        {
          t: "alert",
          k: "i",
          h: "Enter this code on the physical unit — no need to ask the customer for it or type it again here.",
        },
        ...(!isUnitActivated
          ? [
              {
                t: "alert",
                k: "w",
                h: "This unit isn't showing as activated yet — the button below stays disabled until it is.",
              },
            ]
          : []),
        { t: "ro", k: "Unit serial", v: item.context?.serial },
        { t: "ro", k: "Activation code", v: item.context?.activationCode },
        {
          t: "cbx",
          name: "powered",
          l: "Unit powered on and connected to clinic WiFi",
        },
        { t: "cbx", name: "testScan", l: "Test scan completed successfully" },
      ],
      validate: (v) =>
        !item.context?.activationCode
          ? "No activation code found for this clinic's kiosk"
          : !isUnitActivated
            ? "This unit isn't marked as activated yet"
            : !v.powered || !v.testScan
              ? "Both on-site checks are required"
              : null,
      primary: "Activate Air Unit",
      secondary: "Report a problem",
      success: "Unit activated",
      submit: (item, v, token) =>
        apiService.submitActivationCode(
          item.orderId,
          { ...v, activationCode: item.context?.activationCode },
          token,
        ),
    };
  },

  /* TC3W — nothing for the technician to do yet; OL6 (Engagement/Ops Lead
     confirming an inauguration date) hasn't landed. Purely informational, so
     the primary button just closes the drawer rather than calling an API. */
  "activation-waiting": (item) => ({
    title: "Unit activated",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "i",
        h: "This unit is activated. The next step (marking inauguration complete) will appear here once the Engagement/Ops Lead confirms an inauguration date.",
      },
    ],
    primary: "Got it",
    secondary: "Close",
    success: "Noted",
    submit: async () => ({}),
  }),

  /* TC4 */
  "complete-inauguration": (item) => ({
    title: "Complete inauguration",
    sub: orderLine(item),
    body: [
      { t: "alert", k: "i", h: "Mark the launch event complete." },
      {
        t: "two",
        a: {
          t: "ro",
          k: "Scheduled",
          v: item.context?.inaugurationDate,
        },
        b: { t: "ro", k: "Guest", v: item.context?.influencer },
      },
      {
        t: "sel",
        name: "outcome",
        l: "Outcome",
        opts: opts(
          { value: "held", label: "Held as planned" },
          { value: "held_late", label: "Held, rescheduled on the day" },
          { value: "not_held", label: "Not held" },
        ),
      },
      { t: "ta", name: "notes", l: "Notes" },
    ],
    primary: "Mark inauguration complete",
    secondary: "Cancel",
    success: "Inauguration marked complete",
    submit: (item, v, token) =>
      apiService.completeInauguration(item.orderId, v, token),
  }),

  /* TC5 — photo-only now: no checkbox checklist, no separate clinic
     sign-off step. Uploading these four required photos is itself what
     completes the job and activates the order. */
  checklist: (item) => ({
    title: "Installation checklist",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "w",
        h: "Uploading these photos completes the job — the order activates immediately.",
      },
      {
        t: "up",
        name: "photoUnit",
        l: "Installed unit in place",
        s: "Required.",
        accept: "image/*",
      },
      {
        t: "up",
        name: "photoSerial",
        l: "Serial plate",
        s: "Required.",
        accept: "image/*",
      },
      {
        t: "up",
        name: "photoTestScan",
        l: "First successful test scan result",
        s: "Required.",
        accept: "image/*",
      },
      {
        t: "up",
        name: "photoChecklistForm",
        l: "Technician activation and handover checklist",
        s: "Required.",
        accept: "image/*",
      },
      {
        t: "up",
        name: "photosOther",
        l: "Additional photos (optional)",
        s: "Anything else worth keeping on record.",
        accept: "image/*",
        multiple: true,
      },
      { t: "ta", name: "observations", l: "Observations" },
    ],
    validate: (v) => {
      if (!v.photoUnit?.[0]) return "A photo of the installed unit in place is required";
      if (!v.photoSerial?.[0]) return "A photo of the serial plate is required";
      if (!v.photoTestScan?.[0]) return "A photo of the first successful test scan is required";
      if (!v.photoChecklistForm?.[0])
        return "A photo of the technician activation and handover checklist is required";
      return null;
    },
    primary: "Submit and complete job",
    secondary: "Save draft",
    success: "Checklist submitted — order activated",
    submit: (item, v, token) => {
      const { photoUnit, photoSerial, photoTestScan, photoChecklistForm, photosOther, ...rest } = v;
      const photos = [
        photoUnit?.[0],
        photoSerial?.[0],
        photoTestScan?.[0],
        photoChecklistForm?.[0],
        ...(photosOther || []),
      ].filter(Boolean);
      const photoTypes = [];
      if (photoUnit?.[0]) photoTypes.push("unit_in_place");
      if (photoSerial?.[0]) photoTypes.push("serial_plate");
      if (photoTestScan?.[0]) photoTypes.push("test_scan_result");
      if (photoChecklistForm?.[0]) photoTypes.push("checklist_form");
      (photosOther || []).forEach(() => photoTypes.push("other"));
      return apiService.submitChecklist(item.orderId, { ...rest, photos, photoTypes }, token);
    },
  }),

  /* Flow 6 — available whenever a technician can't finish on site. Opened
     directly from the job screen rather than the queue, so it has no TC id
     of its own in SCREEN_BY_ITEM_ID. */
  incomplete: (item) => ({
    title: "Installation incomplete",
    sub: orderLine(item),
    body: [
      {
        t: "alert",
        k: "d",
        h: "This sends the job back to Ops for reassignment. The order will not be activated.",
      },
      {
        t: "sel",
        name: "reason",
        l: "Reason",
        opts: opts(
          { value: "no_network", label: "No network at the clinic" },
          { value: "damaged_unit", label: "Damaged unit" },
          { value: "clinic_not_ready", label: "Clinic not ready" },
          { value: "staff_unavailable", label: "Staff unavailable" },
          { value: "power_issue", label: "Power issue" },
          { value: "other", label: "Other" },
        ),
      },
      { t: "ta", name: "notes", l: "Notes" },
      {
        t: "up",
        name: "photos",
        l: "Add photos",
        s: "Whatever documents the problem.",
        accept: "image/*",
        multiple: true,
      },
    ],
    validate: (v) => (!v.reason ? "A reason is required" : null),
    primary: "Mark incomplete",
    secondary: "Cancel",
    success: "Marked incomplete — sent back to Ops",
    submit: (item, v, token) => apiService.markIncomplete(item.orderId, v, token),
  }),

  /* Reversal — opened from the order cost-view widget on an approved/paid
     claim. Never edits the original; creates a new offsetting record. */
  "reverse-claim": (item) => ({
    title: "Reverse claim",
    sub: [item.claimantName, rupeeFromPaise(item.totalAmountPaise)].filter(Boolean).join(" · "),
    body: [
      {
        t: "alert",
        k: "d",
        h: "This creates a new offsetting claim linked back to the original. The original claim itself is never edited.",
      },
      { t: "ta", name: "reason", l: "Reason for reversal" },
    ],
    validate: (v) => (!String(v.reason || "").trim() ? "A reason is required" : null),
    primary: "Reverse claim",
    secondary: "Cancel",
    success: "Claim reversed",
    submit: (item, v, token) => apiService.reverseExpenseClaim(item.id, v.reason, token),
  }),
};

/** Fallback for servers that send the sheet ID rather than a screen key. */
export const SCREEN_BY_ITEM_ID = {
  SL3: "approve-agreement",
  EO1: "offer-slots",
  EO2: "confirm-attendance",
  EO3: "chase-slot",
  EO4: "assign-influencer",
  EO5: "accept-handover",
  EO6: "introduce-customer",
  OA6: "set-dates",
  OA7: "ready-to-ship",
  OA8: "order-hold",
  OA9: "dispatch",
  AC3: "reconcile",
  AC4: "clear-balance",
  AC5: "raise-invoice",
  OL3: "assign-technician",
  OL4: "assign-technician",
  OL5: "plan-travel",
  OL6: "confirm-inauguration",
  TC1: "job-offer",
  TC3: "activation",
  TC3W: "activation-waiting",
  TC4: "complete-inauguration",
  TC5: "checklist",
};

export const screenKeyFor = (item) =>
  item?.screenKey || SCREEN_BY_ITEM_ID[item?.id] || null;

export default ACTION_SCREENS;
